import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, CheckCircle, Palette, Clock, Upload, X, ChevronRight, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCurrency } from '@/context/RegionContext';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 25 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

const Commissions = () => {
  const [paintingTypes, setPaintingTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [referenceImages, setReferenceImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const { formatPrice } = useCurrency();

  const [form, setForm] = useState({
    name: '', email: '', phone: '', painting_type: '',
    size_preference: '', medium_preference: '', budget_range: '', timeline: '', description: ''
  });

  useEffect(() => {
    axios.get(`${API}/painting-types`).then(r => setPaintingTypes(r.data)).catch(() => {});
  }, []);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSelectType = (type) => {
    setSelectedType(type);
    setForm(prev => ({ ...prev, painting_type: type.title }));
    setShowForm(true);
    setTimeout(() => document.getElementById('order-form')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files).slice(0, 3 - referenceImages.length);
    setReferenceImages(prev => [...prev, ...files].slice(0, 3));
    const newPreviews = files.map(f => URL.createObjectURL(f));
    setPreviews(prev => [...prev, ...newPreviews].slice(0, 3));
  };

  const removeImage = (i) => {
    setReferenceImages(prev => prev.filter((_, idx) => idx !== i));
    setPreviews(prev => { URL.revokeObjectURL(prev[i]); return prev.filter((_, idx) => idx !== i); });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.description.trim()) {
      toast.error('Please fill in required fields'); return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      referenceImages.forEach(img => fd.append('reference_images', img));
      await axios.post(`${API}/commissions`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmitted(true);
      toast.success('Request submitted successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false); setShowForm(false); setSelectedType(null);
    setForm({ name: '', email: '', phone: '', painting_type: '', size_preference: '', medium_preference: '', budget_range: '', timeline: '', description: '' });
    setReferenceImages([]); setPreviews([]);
  };

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="commissions-page">
      <SEO title="Custom Paintings - Gangadhar Som" description="Commission a custom painting. Portraits, devotional art, landscapes, pet portraits, and more." url={`${window.location.origin}/commissions`} />
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }} className="mb-12 sm:mb-16">
          <motion.span variants={fadeUp} className="font-sans uppercase tracking-[0.2em] text-xs font-medium text-muted-foreground">Custom Artwork</motion.span>
          <motion.h1 variants={fadeUp} className="font-serif text-3xl sm:text-4xl md:text-6xl font-light tracking-tight mt-3 mb-5" data-testid="commissions-title">Custom Paintings</motion.h1>
          <motion.p variants={fadeUp} className="font-sans text-sm sm:text-base md:text-lg text-foreground/80 max-w-2xl">
            Choose a painting type below, upload reference images, and describe your vision. Each piece is hand-crafted with meticulous care.
          </motion.p>
        </motion.div>

        {/* Painting Types Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16" data-testid="painting-types-grid">
          {paintingTypes.map((type, i) => (
            <motion.div
              key={type.id}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              onClick={() => handleSelectType(type)}
              className={`group cursor-pointer bg-card border rounded-lg overflow-hidden transition-all hover:shadow-lg hover:border-accent/40 ${selectedType?.id === type.id ? 'ring-2 ring-accent border-accent' : 'border-border'}`}
              data-testid={`painting-type-${type.id}`}
            >
              {type.image_url ? (
                <div className="h-40 bg-muted overflow-hidden">
                  <img src={type.image_url} alt={type.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              ) : (
                <div className="h-40 bg-gradient-to-br from-accent/5 to-accent/15 flex items-center justify-center">
                  <Palette size={36} className="text-accent/30" />
                </div>
              )}
              <div className="p-4 sm:p-5">
                <h3 className="font-serif text-lg font-light mb-1.5">{type.title}</h3>
                <p className="font-sans text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-2">{type.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                    {type.starting_price > 0 && <span>From {formatPrice(type.starting_price)}</span>}
                    {type.turnaround && <span className="flex items-center gap-1"><Clock size={10} /> {type.turnaround}</span>}
                  </div>
                  <ChevronRight size={16} className="text-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Order Form */}
        <AnimatePresence>
          {showForm && (
            <motion.div id="order-form" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.4 }}>
              {submitted ? (
                <div className="bg-card border border-border rounded-lg p-8 sm:p-12 text-center max-w-2xl mx-auto" data-testid="commission-success">
                  <CheckCircle size={48} className="mx-auto text-green-500 mb-5" />
                  <h3 className="font-serif text-2xl sm:text-3xl font-light mb-3">Thank You!</h3>
                  <p className="font-sans text-sm sm:text-base text-muted-foreground max-w-md mx-auto">
                    Your custom painting request has been submitted. We'll review your reference images and get back to you within 2-3 business days.
                  </p>
                  <Button onClick={resetForm} variant="outline" className="mt-6" data-testid="submit-another-btn">Submit Another Request</Button>
                </div>
              ) : (
                <div className="max-w-3xl mx-auto">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="font-serif text-2xl font-light">Request: {selectedType?.title}</h2>
                      <p className="font-sans text-xs text-muted-foreground mt-1">{selectedType?.description}</p>
                    </div>
                    <button onClick={() => { setShowForm(false); setSelectedType(null); }} className="p-2 hover:bg-muted rounded-full"><X size={18} /></button>
                  </div>

                  <form onSubmit={handleSubmit} className="bg-card border border-border rounded-lg p-6 sm:p-8 space-y-5" data-testid="commission-form">
                    {/* Contact */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><Label className="text-xs">Your Name *</Label><Input value={form.name} onChange={e => handleChange('name', e.target.value)} placeholder="Full name" required data-testid="commission-name" /></div>
                      <div><Label className="text-xs">Email *</Label><Input type="email" value={form.email} onChange={e => handleChange('email', e.target.value)} placeholder="your@email.com" required data-testid="commission-email" /></div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="+91 XXXXX XXXXX" data-testid="commission-phone" /></div>
                      <div>
                        <Label className="text-xs">Size Preference</Label>
                        <select value={form.size_preference} onChange={e => handleChange('size_preference', e.target.value)} className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm" data-testid="commission-size">
                          <option value="">Select size</option>
                          <option value="Small (up to 12x16 in)">Small (up to 12x16 in)</option>
                          <option value="Medium (18x24 in)">Medium (18x24 in)</option>
                          <option value="Large (24x36 in)">Large (24x36 in)</option>
                          <option value="Extra Large (36x48 in+)">Extra Large (36x48 in+)</option>
                          <option value="Custom - will discuss">Custom - will discuss</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs">Medium</Label>
                        <select value={form.medium_preference} onChange={e => handleChange('medium_preference', e.target.value)} className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm" data-testid="commission-medium">
                          <option value="">Select medium</option>
                          <option value="Oil on Canvas">Oil on Canvas</option>
                          <option value="Acrylic on Canvas">Acrylic on Canvas</option>
                          <option value="Watercolor">Watercolor</option>
                          <option value="Charcoal/Pencil">Charcoal / Pencil</option>
                          <option value="Digital Art">Digital Art</option>
                          <option value="Artist's Choice">Artist's Choice</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Budget Range</Label>
                        <select value={form.budget_range} onChange={e => handleChange('budget_range', e.target.value)} className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm" data-testid="commission-budget">
                          <option value="">Select range</option>
                          <option value="Under ₹10,000">Under ₹10,000</option>
                          <option value="₹10,000 - ₹25,000">₹10,000 - ₹25,000</option>
                          <option value="₹25,000 - ₹50,000">₹25,000 - ₹50,000</option>
                          <option value="₹50,000 - ₹1,00,000">₹50,000 - ₹1,00,000</option>
                          <option value="Above ₹1,00,000">Above ₹1,00,000</option>
                          <option value="Flexible">Flexible / Discuss</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs">Timeline</Label>
                        <select value={form.timeline} onChange={e => handleChange('timeline', e.target.value)} className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm" data-testid="commission-timeline">
                          <option value="">Select timeline</option>
                          <option value="2-4 weeks">2-4 weeks</option>
                          <option value="1-2 months">1-2 months</option>
                          <option value="2-4 months">2-4 months</option>
                          <option value="4-6 months">4-6 months</option>
                          <option value="Flexible">Flexible</option>
                        </select>
                      </div>
                    </div>

                    {/* Reference Images */}
                    <div>
                      <Label className="text-xs">Reference Images (up to 3)</Label>
                      <p className="text-[11px] text-muted-foreground mb-2">Upload photos or images that inspire your vision</p>
                      <div className="flex gap-3 flex-wrap">
                        {previews.map((p, i) => (
                          <div key={i} className="relative w-24 h-24 rounded-lg overflow-hidden border border-border group">
                            <img src={p} alt={`Reference ${i+1}`} className="w-full h-full object-cover" />
                            <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 p-0.5 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-ref-${i}`}>
                              <X size={12} className="text-white" />
                            </button>
                          </div>
                        ))}
                        {referenceImages.length < 3 && (
                          <label className="w-24 h-24 rounded-lg border-2 border-dashed border-border hover:border-accent/40 flex flex-col items-center justify-center cursor-pointer transition-colors" data-testid="upload-reference-btn">
                            <Upload size={18} className="text-muted-foreground mb-1" />
                            <span className="text-[10px] text-muted-foreground">Add Image</span>
                            <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
                          </label>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <div>
                      <Label className="text-xs">Describe Your Vision *</Label>
                      <Textarea value={form.description} onChange={e => handleChange('description', e.target.value)}
                        placeholder="Describe the painting you'd like. Include details about subject, colors, mood, style, and any specific elements you want..."
                        rows={5} required data-testid="commission-description" />
                    </div>

                    <Button type="submit" disabled={submitting} className="w-full bg-accent hover:bg-accent/90 py-3" data-testid="commission-submit-btn">
                      <Send size={14} className="mr-2" /> {submitting ? 'Submitting...' : 'Submit Custom Painting Request'}
                    </Button>
                  </form>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Commissions;
